import {
    useContext,
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type INavigationMenuFeatureProps from "./type";
import type {
    NavigationMenuFeatureWebPropsType
} from "./type";
import stylesheet, {
    NAVIGATION_MENU_FEATURE_DESCRIPTION_VARIANT,
    NAVIGATION_MENU_FEATURE_ACTION_VARIANT,
    NAVIGATION_MENU_FEATURE_TITLE_VARIANT,
    getNavigationMenuFeatureRadius,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    useInteractionState
} from "../../../../helpers/interaction";
import {
    NavigationMenuLayoutContext,
    useNavigationMenuContext
} from "../../context";
import {
    ArrowRight as ArrowRightIcon
} from "lucide-react-native";
import {
    getMirrorStyle
} from "../../../../helpers/direction";
import {
    NAVIGATION_MENU_TRACK_DATA_KEY
} from "../../util";
import {
    openLink
} from "../../../../helpers/link";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const NavigationMenuFeature: FC<INavigationMenuFeatureProps> = ({
    customTheme: customThemeProp,
    icon: IconComponentProp,
    actionTitle,
    subTitle,
    onPress,
    style,
    title,
    href
}) => {
    const context = useNavigationMenuContext({
        part: "Feature"
    });

    const layout = useContext(NavigationMenuLayoutContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        shapeConfigs,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({});

    const {
        container: containerDynamicStyle,
        action: actionDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        isFocusVisible,
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    });

    if(!context) {
        return null;
    }

    const handlePress = () => {
        onPress?.();

        context.handleLinkPress();

        openLink({
            href
        });
    };

    const webProps: NavigationMenuFeatureWebPropsType = Platform.OS === "web" ? {
        dataSet: {
            [INTERACTION_STATE_DATA_KEY]: interactionHandlers.dataSet?.ncoreState ?? "rest",
            [NAVIGATION_MENU_TRACK_DATA_KEY]: "true"
        },
        href
    } : {};

    const renderIcon = () => {
        if(!IconComponentProp) {
            return null;
        }

        return <View
            style={[
                stylesheet.icon,
                iconDynamicStyle
            ]}
            aria-hidden={true}
        >
            <IconComponentProp
                customColor={colors.content.icon.high}
                size={iconSizes.large}
                color="high"
            />
        </View>;
    };

    const renderAction = () => {
        if(!actionTitle) {
            return null;
        }

        return <View
            style={[
                stylesheet.action,
                actionDynamicStyle
            ]}
        >
            <Text
                variant={NAVIGATION_MENU_FEATURE_ACTION_VARIANT}
                customTheme={customTheme}
                color="emphasized"
            >
                {actionTitle}
            </Text>
            <View
                style={getMirrorStyle({
                    direction
                })}
                aria-hidden={true}
            >
                <ArrowRightIcon
                    color={colors.content.text.emphasized}
                    size={iconSizes.small}
                />
            </View>
        </View>;
    };

    return <Pressable
        {...interactionHandlers}
        {...webProps}
        style={[
            style,
            stylesheet.container,
            layout.isSheet ? stylesheet.sheetContainer : null,
            containerDynamicStyle
        ]}
        onPress={handlePress}
        role="link"
    >
        <StateLayer
            borderRadius={getNavigationMenuFeatureRadius({
                radiuses,
                borders,
                spaces
            })}
            customTheme={customTheme}
            overlayColor="default"
            isPressed={isPressed}
            isHovered={isHovered}
        />
        {renderIcon()}
        <Text
            variant={NAVIGATION_MENU_FEATURE_TITLE_VARIANT}
            customTheme={customTheme}
            color="high"
        >
            {title}
        </Text>
        {
            subTitle ?
                <Text
                    variant={NAVIGATION_MENU_FEATURE_DESCRIPTION_VARIANT}
                    customTheme={customTheme}
                    color="mid"
                >
                    {subTitle}
                </Text>
            :
                null
        }
        {renderAction()}
    </Pressable>;
};
export default NavigationMenuFeature;
